import { useCallback } from 'react';
import { AccountRole } from '@/types/enums';
import { TeamManagementProps } from '@/types/team';
import { fetchAPI } from '@/api/fetch';
import {
  apiInviteTeam,
  apiRemoveInvitation,
  apiRemoveMember,
  apiUpdateMemberRole
} from '@/api/services';

type MemberData = {
  account_id: string;
  role: AccountRole;
};

export const useTeamManagement = ({
  setTeamMembers,
  setConfirmationDialog,
  closeConfirmationDialog
}: TeamManagementProps) => {
  const handleInviteMember = useCallback(
    (accountInvitation: { email: string; role: AccountRole }) => {
      setTeamMembers((prev) => [
        ...prev,
        {
          account_id: null,
          name: accountInvitation.email.split('@')[0],
          email: accountInvitation.email,
          role: accountInvitation.role,
          status: 'invited' as const
        }
      ]);
    },
    [setTeamMembers]
  );

  const handleResendInvitation = useCallback(
    async (email: string, role: AccountRole) => {
      try {
        setConfirmationDialog((prev) => ({
          ...prev,
          isLoading: true,
          error: null
        }));
        const response = await fetchAPI<void>({
          request: () => apiInviteTeam({ email, role })
        });
        if (!response.success) {
          setConfirmationDialog((prev) => ({
            ...prev,
            error: response.message || 'Failed to send invitation'
          }));
          return;
        }
        closeConfirmationDialog();
      } catch {
        setConfirmationDialog((prev) => ({
          ...prev,
          error: 'Failed to send invitation. Please try again.'
        }));
      } finally {
        setConfirmationDialog((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [setConfirmationDialog, closeConfirmationDialog]
  );

  const handleRemoveInvitation = useCallback(
    async (email: string) => {
      try {
        setConfirmationDialog((prev) => ({
          ...prev,
          isLoading: true,
          error: null
        }));
        const response = await fetchAPI<void>({
          request: () => apiRemoveInvitation({ email })
        });
        if (!response.success) {
          setConfirmationDialog((prev) => ({
            ...prev,
            error: response.message || 'Failed to remove member'
          }));
          return;
        }
        setTeamMembers((prev) => prev.filter((member) => member.email !== email));
        closeConfirmationDialog();
      } catch {
        setConfirmationDialog((prev) => ({
          ...prev,
          error: 'Failed to remove member. Please try again.'
        }));
      } finally {
        setConfirmationDialog((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [setConfirmationDialog, setTeamMembers, closeConfirmationDialog]
  );

  const handleRemoveMember = useCallback(
    async (account_id: string) => {
      try {
        setConfirmationDialog((prev) => ({
          ...prev,
          isLoading: true,
          error: null
        }));
        const response = await fetchAPI<void>({
          request: () => apiRemoveMember({ account_id })
        });
        if (!response.success) {
          setConfirmationDialog((prev) => ({
            ...prev,
            error: 'Failed to remove member. Please try again.'
          }));
          return;
        }
        setTeamMembers((prev) => prev.filter((member) => member.account_id !== account_id));
        closeConfirmationDialog();
      } catch {
        setConfirmationDialog((prev) => ({
          ...prev,
          error: 'Failed to remove member. Please try again.'
        }));
      } finally {
        setConfirmationDialog((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [setConfirmationDialog, setTeamMembers, closeConfirmationDialog]
  );

  const handleUpdateRole = useCallback(
    async (role: AccountRole) => {
      try {
        let currentMember: MemberData | null = null;

        setConfirmationDialog((prev) => {
          if (!prev.member || !prev.member.account_id) return prev;
          currentMember = {
            account_id: prev.member.account_id,
            role: prev.member.role
          };
          return { ...prev, isLoading: true, error: null };
        });

        if (!currentMember) {
          setConfirmationDialog((prev) => ({
            ...prev,
            error: 'Invalid member data'
          }));
          return;
        }

        const { account_id } = currentMember;
        const response = await fetchAPI<void>({
          request: () =>
            apiUpdateMemberRole({
              account_id,
              role
            })
        });
        if (response.success) {
          setTeamMembers((prev) =>
            prev.map((m) => (m.account_id === account_id ? { ...m, role } : m))
          );
          closeConfirmationDialog();
        } else {
          setConfirmationDialog((prev) => ({
            ...prev,
            error: response.message || 'Failed to update role'
          }));
        }
      } catch {
        setConfirmationDialog((prev) => ({
          ...prev,
          error: 'Failed to update role. Please try again.'
        }));
      } finally {
        setConfirmationDialog((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [setConfirmationDialog, setTeamMembers, closeConfirmationDialog]
  );

  return {
    handleInviteMember,
    handleResendInvitation,
    handleRemoveInvitation,
    handleRemoveMember,
    handleUpdateRole
  };
};
